import React, { useState } from "react";
import {
	Button,
	Card,
	CardBody,
	CardHeader,
	CardTitle,
	Form,
	Row,
} from "reactstrap";

import history from "../../utils/createBrowserHistory";
import formNavigationUtils from "../../utils/formNavigationUtils";
import LoadingSpinner from "../../components/loadingSpinner/LoadingSpinner";
import FormattedMaskInput from "../../components/formattedMaskInput/FormattedMaskInput";
import InputElement from "../../features/forms/components/InputElement";

import {
	failAlert,
	successAlertCustom,
	twoOptionsAlert,
} from "../../utils/alertUtils";
import { processDigitzsAchTransaction } from "../../api/PaymentsAPI";
import {
	useDigitzsMerchantId,
	useFromUser,
} from "../../stores/LocalStorageHelper";
import { PATHS } from "../../constants/Constants";
import {
	CurrencyInputElement,
	SelectorElement,
} from "../../features/forms/components/InputElement";

import { yupResolver } from "@hookform/resolvers";
import { FormProvider, useForm } from "react-hook-form";
import useSetAccount from "../../account/useSetAccount";
import useSetDeal from "../../features/deal/hooks/useSetDeal";
import * as yup from "yup";

const DigitzsAchPayment = ({
	pmtAmount,
	// relation ot other tables
	accountID = null,
	dealID = null,
	pickupPaymentID = null,
	// amount should add up to these paid values excluding convenience fee
	loanPaid = null,
	lateFeePaid = null,
	adjustmentPaid = null,
	pickupPaid = null,
	convenienceFee = null,
}) => {
	const dealerID = useFromUser("dealerID");
	const locationID = useFromUser("locationID");
	const settingsPage = PATHS.SETTINGS;
	const merchantId = useDigitzsMerchantId();
	const setAccount = useSetAccount();
	const setDeal = useSetDeal();
	const [showPaymentProgress, setShowPaymentProgress] = useState(false);
	const [amount, setAmount] = useState(pmtAmount || 0);
	const [accountType, setAccountType] = useState("checking");
	const [bankName, setBankName] = useState("");
	const [accountName, setAccountName] = useState("");
	const [accountNumber, setAccountNumber] = useState(null);
	const [routingNumber, setRoutingNumber] = useState(null);

	// Asks if user wants to sign up for Digitzs
	const askToSignUp = async () => {
		const didConfirm = await twoOptionsAlert(
			"No Digitzs account found. Do you want to sign up for Digitzs?",
			"Yes",
			"No"
		);

		if (didConfirm === "Yes") {
			history.push({
				pathname: settingsPage,
				state: {
					tab: "signUp",
				},
			});
		}
	};

	// Checks that amount is at least 1, per digitzs requirement
	const isAmountValid = () => {
		if (amount < 1) {
			methods.setError("digitzsAmount", {
				message: "Must be at least 1",
				shouldFocus: true,
			});

			return false;
		}

		return true;
	};

	// Calls api to process payment if all data is valid
	const processPayment = () => {
		if (!isAmountValid()) {
			return;
		}

		if (merchantId !== null) {
			setShowPaymentProgress(true);

			const paymentData = {
				dealerID,
				locationID,
				accountID,
				dealID,

				accountID,
				dealID,
				pickupPaymentID,
				// amount should add up to these paid values excluding convenience fee
				loanPaid,
				lateFeePaid,
				adjustmentPaid,
				pickupPaid,
				convenienceFee,
				// amount is only included in ACH request
				amount,
				bankName,
				accountName,
				accountType,
				accountNumber,
				routingNumber,
			};

			processDigitzsAchTransaction(paymentData).then(
				(res) => {
					// this can return either a deal or account object.
					if (paymentData.accountID !== null) {
						// set account obj
						setAccount(res);
					} else if (paymentData.dealID !== null) {
						// set deal obj
						setDeal(res.data.content);
					}
					setShowPaymentProgress(false);
					successAlertCustom(res.data.message);
				},
				(err) => {
					setShowPaymentProgress(false);
					failAlert(
						err?.response?.data?.message || "Failed to process payment"
					);
				}
			);
		} else {
			askToSignUp();
		}
	};
	// Define rules for each input field
	const schema = yup.object().shape({});

	// Define form validation parameters
	const methods = useForm({
		reValidateMode: "onBlur",
		resolver: yupResolver(schema),
		shouldFocusError: true,
	});

	return (
		<div className="col-lg-12 mx-auto">
			<FormProvider {...methods}>
				<Form onKeyDown={formNavigationUtils}>
					<Card className="pt-2">
						<CardHeader>
							<CardTitle>
								<h3>Payment Terminal</h3>
							</CardTitle>
						</CardHeader>
						{(showPaymentProgress && <LoadingSpinner />) || (
							<div>
								<CardBody className="mx-1">
									<Row>
										<CurrencyInputElement
											{...methods}
											value={amount}
											onChange={setAmount}
											name="digitzsAmount"
											placeholder="Enter amount to charge"
											label="Amount"
										/>
									</Row>
									<Row>
										<SelectorElement
											{...methods}
											name="accountType"
											label="Account Type"
											value={accountType}
											onChange={(e) => setAccountType(e.target.value)}
											options={[
												{ value: "checking", label: "Checking" },
												{ value: "savings", label: "Savings" },
											]}
											labelClass="required"
										/>
										<InputElement
											{...methods}
											colSize="col-lg-3"
											value={bankName}
											onChange={(e) => setBankName(e.target.value)}
											name="digitzsBankName"
											label="Bank Name"
											placeholder=""
											type="text"
										/>
										<InputElement
											{...methods}
											colSize="col-lg-3"
											value={accountName}
											onChange={(e) => setAccountName(e.target.value)}
											name="digitzsAccountName"
											label="Name on Account"
											placeholder=""
											type="text"
										/>
										<FormattedMaskInput
											{...methods}
											colSize="col-lg-4"
											format="##########"
											value={accountNumber}
											onChange={setAccountNumber}
											name="digitzsAccountNumber"
											label="Account Number"
										/>
										<FormattedMaskInput
											{...methods}
											colSize="col-lg-4"
											format="##########"
											value={routingNumber}
											onChange={setRoutingNumber}
											name="digitzsRoutingNumber"
											label="Routing Number"
										/>
									</Row>
								</CardBody>
								<CardBody className="row mx-1 mt-3">
									<div className="col-12 d-flex justify-content-center my-3">
										<Button
											color="primary"
											onClick={methods.handleSubmit(processPayment)}
										>
											Process Payment
										</Button>
									</div>
								</CardBody>
							</div>
						)}
					</Card>
				</Form>
			</FormProvider>
		</div>
	);
};

export default DigitzsAchPayment;
